import { Suspense } from 'react';

import { TFormalOffer } from '@helpers/FormalOffers/formalOffers.types';
import { FormattedCurrency } from '@iwIntlProvider/FormattedCurrency';
import { fetchPutFormalOffersCreditFacilityConfiguration } from '@iwoca/lapi-client/edge';
import { Button, Card, Pill, Text, Tooltip } from '@iwoca/orion';
import { maintainStateKey } from '@iwRouter/LocationHelper';
import { useMutation } from '@tanstack/react-query';
import { Skeleton } from '@ui';
import { FormattedMessage } from 'react-intl';
import { useHistory } from 'react-router-dom';

import { FirstMonthlyRepayment } from './FirstMonthlyRepayment';
import { ViewPaymentPlan } from './ViewPaymentPlan';
import { ErrorBoundary } from '../../../../errorBoundary/ErrorBoundary';
import { useStateKey } from '../../../../iwQueryClient/useStateKey';
import FallbackErrorModal from '../../../modals/fallbackErrorModal/FallbackErrorModal';
import { isSelectedAmountValid } from '../../../ui/ChooseAmountInputWithSlider/isSelectedAmountValid';
import { Rate } from '../../../ui/rate';
import { useGetDiscountedPrice } from '../useGetDiscountedPrice';

export const OfferDetails = ({
    selectedAmount,
    offer
}: {
    selectedAmount: number | null;
    offer: TFormalOffer;
}) => {
    return (
        <ErrorBoundary
            fallback={(error) => (
                <Card data-testid="rcf-offer-details-error" className="p-xl">
                    <FallbackErrorModal error={error} />
                </Card>
            )}
        >
            <Suspense
                fallback={
                    <Card
                        data-testid="rcf-offer-details-loading"
                        className="space-y-xl p-xl"
                    >
                        <Skeleton className="h-[25px] w-full" />
                        <Skeleton className="h-[25px] w-full" />
                        <Skeleton className="h-[25px] w-full" />
                        <Skeleton className="h-[25px] w-full" />
                        <Skeleton className="h-[25px] w-full" />
                        <Skeleton className="h-[25px] w-full" />
                    </Card>
                }
            >
                <Details selectedAmount={selectedAmount} offer={offer} />
            </Suspense>
        </ErrorBoundary>
    );
};

const Details = ({
    selectedAmount,
    offer
}: {
    selectedAmount: number | null;
    offer: TFormalOffer;
}) => {
    const history = useHistory();
    const stateKey = useStateKey();

    const { hasDiscountedPricing } = useGetDiscountedPrice({
        enabled: true,
        formalOffer: offer
    });

    const createOfferConfigurationMutation = useMutation({
        mutationKey: ['createOfferConfiguration'],
        mutationFn: ({
            offerId,
            drawdownAmount
        }: {
            offerId: string;
            drawdownAmount: number;
        }) =>
            fetchPutFormalOffersCreditFacilityConfiguration({
                stateKey,
                offerId,
                body: {
                    data: {
                        cash_amount: drawdownAmount
                    }
                }
            })
    });

    const creditLimit = offer.max_amount!;
    const interestFreePeriodMonths = offer.interest_only_repayments;
    const fixedFee =
        offer.proposed_access_period_details!
            .access_period_opening_fee_max_amount!;
    const interestRatePer30Days = offer.interest_rates![0].rate!;

    const drawdownAmount = selectedAmount ?? creditLimit;

    const goToFundingRequirements = async () => {
        await createOfferConfigurationMutation.mutateAsync({
            offerId: offer.offer_id!,
            drawdownAmount
        });

        history.push(
            maintainStateKey({
                pathname: '/funding-requirements/',
                search: `?offerId=${offer.offer_id!}&amount=${drawdownAmount}`
            })
        );
    };

    return (
        <Card className="p-xl">
            <div className="w-full space-y-xl">
                <Text className="text-l font-bold">
                    <FormattedMessage
                        id="_.rcf_approved_view.offer_details_title"
                        defaultMessage="Your offer details"
                    />
                </Text>
                <div className="flex items-center justify-between">
                    <Text>
                        <FormattedMessage
                            id="_.rcf_approved_view.offer_details_credit_limit"
                            defaultMessage="Credit limit"
                        />
                    </Text>
                    <Text
                        className="text-l font-bold"
                        data-testid="offer-details-credit-limit"
                    >
                        <FormattedCurrency value={creditLimit} />
                    </Text>
                </div>
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-s">
                        <Text>
                            <FormattedMessage
                                id="_.rcf_approved_view.offer_details_interest_free_period"
                                defaultMessage="Interest-free period"
                            />
                        </Text>
                        <Tooltip
                            data-testid="offer-details-interest-free-period-tooltip"
                            // @ts-expect-error
                            content={
                                <FormattedMessage
                                    id="_.rcf_approved_view.offer_details_interest_free_period_tooltip"
                                    defaultMessage="During this period, you will only pay interest on the outstanding monthly amount. Extension possible."
                                />
                            }
                        />
                    </div>
                    <Text className="text-l font-bold">
                        <FormattedMessage
                            id="_.rcf_approved_view.offer_details_interest_free_period_months"
                            defaultMessage="{months} months"
                            values={{ months: interestFreePeriodMonths }}
                        />
                    </Text>
                </div>
                <div className="flex items-center justify-between">
                    <Text>
                        <FormattedMessage
                            id="_.rcf_approved_view.offer_details_fixed_fee"
                            defaultMessage="Fixed fee (one off)"
                        />
                    </Text>
                    <Text className="text-l font-bold">
                        <FormattedCurrency value={fixedFee} />
                    </Text>
                </div>
                <div className="flex items-center justify-between">
                    <Text>
                        <FormattedMessage
                            id="_.rcf_approved_view.interest_rate_per_30_days"
                            defaultMessage="Interest rate per 30 days"
                        />
                    </Text>
                    <Text className="text-right text-l font-bold">
                        {hasDiscountedPricing && (
                            <>
                                <div>
                                    <Pill>
                                        <FormattedMessage
                                            id="_.rcf_approved_view.offer_details_discounted_offer"
                                            defaultMessage="Our offer for you:"
                                        />
                                    </Pill>
                                </div>
                                <span className="font-reg line-through">
                                    2,80&nbsp;%
                                </span>{' '}
                            </>
                        )}
                        <Rate value={interestRatePer30Days} />
                    </Text>
                </div>
                <FirstMonthlyRepayment
                    drawdownAmount={drawdownAmount}
                    offer={offer}
                />
            </div>

            <div className="my-xl">
                <ViewPaymentPlan
                    drawdownAmount={drawdownAmount}
                    offer={offer}
                />
            </div>
            <Button
                onClick={goToFundingRequirements}
                disabled={!isSelectedAmountValid(drawdownAmount, offer)}
                loading={createOfferConfigurationMutation.isLoading}
                variant="success"
                className="w-full"
            >
                <FormattedMessage
                    id="_.rcf_approved_view.offer_details_proceed_with_offer"
                    defaultMessage="Proceed with this offer"
                />
            </Button>
        </Card>
    );
};
